import type { FlightCardData, FlightPricing } from '@massilia/queries'

import { Button, Popover, PopoverContent, PopoverTrigger, Separator } from '@massilia/ui'
import { HelpCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useAgencyPhone } from '@/hooks/use-agency-phone'
import { PASSENGER_TYPE_LABELS } from '@/utils/passenger-labels'

import { useDebug } from './debug-context'
import { FlightConditionsModal } from './flight-conditions-modal'
import { InstallmentMention } from './installment-mention'

interface FlightCardPriceProps {
  flight: FlightCardData
  onSelect?: () => void
  pricing: FlightPricing
  selectedJourneyKeys: string[]
  transactionKey: string
}

type PerPassengerMargin = NonNullable<FlightPricing['breakdown']['margin']>['perPassenger'][number]

/**
 * Zone de prix et d'action de la carte de vol.
 *
 * Mobile : une barre en pied de carte — prix à gauche, bouton à droite, mentions
 * sur une micro-ligne. Desktop : la colonne latérale historique de 17rem.
 * L'ancien rendu empilait la colonne desktop telle quelle sur mobile, ce qui
 * coûtait 188px pour un prix et un bouton.
 */
export function FlightCardPrice({
  flight,
  onSelect,
  pricing,
  selectedJourneyKeys,
  transactionKey,
}: FlightCardPriceProps) {
  const showDebug = useDebug()
  const agencyPhone = useAgencyPhone()
  const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false)
  // Get first passenger price for display - prioritize adult, fallback to first available
  const adultPricing = pricing.perPassenger.find((p) => p.type === 'Adult')
  const displayPricing = adultPricing ?? pricing.perPassenger[0] ?? 0
  const pricePerPerson = Number.parseInt(
    displayPricing?.pricePerPerson.amount ?? pricing.total.amount
  )
  const totalePrice = Number.parseInt(pricing.total.amount)
  const passengerTypeLabel = displayPricing
    ? PASSENGER_TYPE_LABELS[displayPricing.type].toLowerCase()
    : 'personne'

  const shellClassName =
    'flex w-full flex-col gap-2 border-y border-gray-100 bg-gray-50 px-3 py-2.5 lg:h-full lg:w-68 lg:justify-between lg:border-y-0 lg:border-l lg:px-5 lg:py-4'

  if (flight.callAgencyOnly) {
    return (
      <div className={shellClassName}>
        <div className="flex items-center justify-between gap-3 lg:flex-col lg:items-stretch lg:gap-3 lg:py-4">
          <p className="text-sm font-medium text-gray-700 lg:text-center">
            Pour cette offre, contactez l&apos;agence
          </p>
          <a
            className="shrink-0 rounded-md bg-linear-to-br from-orange-500 to-[#D84A2D] px-4 py-2.5 text-center text-sm font-medium whitespace-nowrap text-white transition-opacity hover:opacity-90 lg:text-base"
            href={`tel:${agencyPhone.replaceAll(/\s/g, '')}`}
          >
            {agencyPhone}
          </a>
        </div>

        {showDebug && <PriceDebug pricing={pricing} />}
      </div>
    )
  }

  return (
    <div className={shellClassName}>
      <div className="flex items-center justify-between gap-3 lg:flex-col lg:items-stretch lg:gap-3">
        <div className="flex min-w-0 flex-col gap-0.5 lg:gap-2">
          {/*
            Même hiérarchie qu'en desktop : le prix par passager en gros, celui
            qu'on compare d'une offre à l'autre, et le total TTC juste à côté —
            le chiffre qu'on paie, et celui qu'impose le règlement CE 1008/2008.
            Sur mobile les deux tiennent sur une ligne, faute de place pour deux
            blocs empilés ; `flex-wrap` les sépare sur les montants à cinq
            chiffres.
          */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 lg:hidden">
            <span className="flex items-baseline gap-x-1">
              <span className="text-xl leading-tight font-bold whitespace-nowrap text-brand-600">
                {pricePerPerson} €
              </span>
              <span className="text-[11px] whitespace-nowrap text-gray-500">
                / {passengerTypeLabel}
              </span>
            </span>
            <span className="text-[11px] whitespace-nowrap text-gray-600">
              Total <span className="font-semibold">{totalePrice} € TTC</span>
            </span>
          </div>

          <div className="hidden lg:block">
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="text-2xl leading-tight font-bold whitespace-nowrap text-brand-600">
                {pricePerPerson} €
              </span>
              <span className="text-sm whitespace-nowrap text-gray-500">
                / par {passengerTypeLabel}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2">
              <span className="text-base font-medium text-gray-600">Total</span>
              <span className="text-lg font-bold whitespace-nowrap text-brand-600">
                {totalePrice} € TTC
              </span>
            </div>
          </div>

          {/*
            La mention de financement se lit sous le prix qu'elle concerne, des
            deux côtés : c'est une information de PAIEMENT, sa place est la
            colonne du prix et du bouton, pas la section horaires. Sur mobile
            elle tient dans la hauteur que le bouton occupe déjà, sans coûter
            une ligne ; sur desktop elle passe en `full`, avec le logo du
            financeur et l'explication sur deux lignes.
          */}
          <div className="text-[11px] leading-tight text-gray-500 lg:hidden">
            <InstallmentMention flight={flight} />
          </div>
          <div className="mt-2 hidden border-t border-gray-200 pt-2 lg:block">
            <InstallmentMention flight={flight} variant="full" />
          </div>
        </div>

        <Button
          className="h-10 shrink-0 bg-linear-to-br from-orange-500 to-[#D84A2D] px-5 text-sm text-white hover:cursor-pointer hover:opacity-90 lg:w-full lg:text-base"
          onClick={() => {
            onSelect?.()
          }}
        >
          {/*
            Deux libellés exclusifs plutôt qu'un suffixe : le bouton est un flex
            `gap-2`, et un second enfant visible y ajoutait un blanc de 8px au
            milieu du libellé.
          */}
          <span className="lg:hidden">Choisir</span>
          <span className="hidden lg:inline">Choisir ce vol</span>
        </Button>
      </div>

      {/* Liens de détail, sous le geste principal. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] leading-tight text-gray-500 lg:flex-col lg:items-start lg:gap-y-1.5 lg:text-xs">
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="flex cursor-pointer items-center gap-1 underline underline-offset-2 hover:text-gray-800"
              type="button"
            >
              Détail des prix
              <HelpCircle className="size-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-auto max-w-xs border-none bg-orange-500 p-3 text-sm text-white shadow-lg"
            side="top"
            sideOffset={6}
          >
            <div className="space-y-1">
              {pricing.perPassenger.map((pp, index) => (
                <div className="flex justify-between gap-4" key={`${pp.type}-${index}`}>
                  <span>
                    {pp.count} x {PASSENGER_TYPE_LABELS[pp.type]}
                  </span>
                  <span>{Number(pp.pricePerPerson.amount).toFixed(0)}€</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between gap-4 border-t border-orange-400 pt-2 font-medium">
                <span>Total</span>
                <span>{Number(pricing.total.amount).toFixed(0)}€</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <button
          className="cursor-pointer underline underline-offset-2 hover:text-gray-800"
          onClick={() => {
            setIsConditionsModalOpen(true)
          }}
          type="button"
        >
          Conditions du billet
        </button>
      </div>

      {showDebug && <PriceDebug pricing={pricing} />}

      <FlightConditionsModal
        flight={flight}
        isOpen={isConditionsModalOpen}
        onClose={() => {
          setIsConditionsModalOpen(false)
        }}
        onSelectFlight={onSelect}
        selectedJourneyKeys={selectedJourneyKeys}
        transactionKey={transactionKey}
      />
    </div>
  )
}

/** Sur une marge normale combinée à un allotement mono-sens, précise si
 * l'allotement couvrait l'aller ou le retour (sinon les deux sens sont couverts). */
function getAllotmentSideLabel(pm: PerPassengerMargin): string {
  const hasOutbound =
    pm.allotmentOutboundAmount !== null && pm.allotmentOutboundAmount !== undefined
  const hasReturn = pm.allotmentReturnAmount !== null && pm.allotmentReturnAmount !== undefined

  if (hasOutbound && !hasReturn) return 'aller'
  if (hasReturn && !hasOutbound) return 'retour'
  return 'un sens'
}

/** Détail (debug) de l'identifiant de marge d'un passager : allotement + normale
 * combinées, sinon une marge simple, sinon rien. */
function MarginIdDetail({ pm }: { pm: PerPassengerMargin }) {
  const { allotmentMarginId, marginId, normalMarginId } = pm

  if (allotmentMarginId || normalMarginId) {
    return (
      <span className="ml-2 flex flex-col gap-0.5">
        {allotmentMarginId && (
          <span className="flex flex-col text-purple-700">
            <span>
              ↳ Marge allotement: <b>{pm.allotmentMarginPerPerson}€</b>/pax
              {pm.allotmentOutboundAmount && pm.allotmentReturnAmount && (
                <span className="ml-1 text-purple-500">
                  (aller: {pm.allotmentOutboundAmount}€ / retour: {pm.allotmentReturnAmount}€)
                </span>
              )}
            </span>
            <button
              className="cursor-pointer text-left text-purple-500 hover:underline"
              onClick={() => {
                void navigator.clipboard.writeText(allotmentMarginId).then(() => {
                  toast.info('Identifiant de marge allotement copié')
                })
              }}
              title="Cliquez pour copier"
              type="button"
            >
              {allotmentMarginId}
            </button>
          </span>
        )}
        {normalMarginId && (
          <span className="flex flex-col text-blue-700">
            <span>
              ↳ Marge normale: <b>{pm.normalMarginPerPerson}€</b>/pax
              {pm.normalMarginMultiplier !== null &&
                pm.normalMarginMultiplier !== undefined &&
                pm.normalMarginMultiplier !== 1 && (
                  <span className="ml-1 text-blue-500">
                    ({pm.normalMarginPerPersonRaw}€ × {pm.normalMarginMultiplier} — allotement{' '}
                    {getAllotmentSideLabel(pm)} seul)
                  </span>
                )}
            </span>
            <button
              className="cursor-pointer text-left text-blue-500 hover:underline"
              onClick={() => {
                void navigator.clipboard.writeText(normalMarginId).then(() => {
                  toast.info('Identifiant de marge normale copié')
                })
              }}
              title="Cliquez pour copier"
              type="button"
            >
              {normalMarginId}
            </button>
          </span>
        )}
      </span>
    )
  }

  if (marginId) {
    return (
      <button
        className="cursor-pointer text-left text-yellow-600 hover:underline"
        onClick={() => {
          void navigator.clipboard.writeText(marginId).then(() => {
            toast.info('Identifiant de marge copié dans le presse-papier')
          })
        }}
        title="Cliquez pour copier dans le presse-papier"
        type="button"
      >
        {marginId}
      </button>
    )
  }

  return <span className="text-gray-400">Aucune marge</span>
}

/** Bloc de diagnostic tarifaire, ouvert d'office comme celui de `FlightInfoSection`. */
function PriceDebug({ pricing }: { pricing: FlightPricing }) {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div className="rounded border border-yellow-200 bg-yellow-50/60">
      <button
        aria-expanded={isOpen}
        className="w-full cursor-pointer px-2 py-1 text-left font-mono text-[11px] font-bold text-yellow-800"
        onClick={() => {
          setIsOpen((v) => !v)
        }}
        type="button"
      >
        {isOpen ? '▾' : '▸'} debug prix
        {pricing.accountCode && <span className="ml-1 text-red-700">{pricing.accountCode}</span>}
      </button>

      {isOpen && (
        <div className="flex flex-col gap-2 border-t border-yellow-200 px-2 py-1.5 font-mono text-[11px] break-all text-yellow-900">
          {pricing.accountCode && (
            <span className="font-bold text-red-800">CODE: {pricing.accountCode}</span>
          )}

          {pricing.xRoles && pricing.xRoles.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {pricing.xRoles.map((role) => (
                <span
                  className="rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 font-bold text-purple-800"
                  key={role}
                >
                  {role}
                </span>
              ))}
            </div>
          )}

          {pricing.breakdown.margin && (
            <div className="flex flex-col gap-2">
              <span>
                <b>
                  {(
                    Number(pricing.breakdown.baseFare.amount) +
                    Number(pricing.breakdown.taxes.amount)
                  ).toFixed(2)}
                </b>
                € Total TTC (base)
              </span>
              <Separator orientation="horizontal" />
              {/* Per-passenger margin breakdown with margin IDs */}
              {pricing.breakdown.margin.perPassenger.length > 0 && (
                <span className="flex flex-col gap-1">
                  <b>Marge par passager:</b>
                  {pricing.breakdown.margin.perPassenger.map((pm, idx) => (
                    <span className="ml-2 flex flex-col" key={idx}>
                      <span>
                        {pm.count} x {pm.type}: <b>{pm.marginPerPerson}€</b>/pax ={' '}
                        {(pm.count * Number.parseFloat(pm.marginPerPerson)).toFixed(2)}€
                      </span>
                      {/* Détail des marges combinées (allotement + normale) */}
                      <MarginIdDetail pm={pm} />
                    </span>
                  ))}
                </span>
              )}
              <Separator orientation="horizontal" />
              <span>
                <b>{pricing.breakdown.margin.amount}</b>€ Marge totale
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
